import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { Icon } from '../ui/Icon'
import { ArtifactSettings } from './ArtifactSettings'
import { artifactRemove } from '../transport/api'
import type { SharedArtifact } from '../transport/relay'
import { ownFrameHere, ownFrameNeedsCloud, sharedFrameHere, type ContentFrame } from './contentHost'
import { explainShareError } from './explainShareError'
import {
  artifactState,
  inAppViewGate,
  sharerOf,
  type ArtifactRow,
  type ArtifactsEverywhere,
} from './everywhere'

/**
 * THE IN-APP BROWSER — an artifact, opened inside the app, with no address bar.
 *
 * Owner, 2026-09-20: 「讓 Artifact 這一個欄位,使用者可以直接點擊並且使用 App 的 In-App
 * Browser 打開…因為是 In-App Browser,所以直接 identify 使用者的身分,就可以決定是否要
 * 打開,除非使用 App 外部的 Browser,那需要做身份確認。而且 In-App Browser 不要給任何的
 * 網址欄位讓使用者可以輸入,純粹就是用來觀看 Artifact 這個網頁就好了。然後…可以在 Header
 * Bar 也是最上方,增加讓使用者可以設定的這些方法,比如說 Share Button、使用者權限的開啟,
 * 這些功能的設定都可以使用一個下拉選單來調整」.
 *
 * THERE IS NO URL FIELD AND THERE MUST NOT BE ONE. Not an oversight to be tidied
 * up later by someone adding "just a read-only address" — this surface exists to
 * show ONE artifact, and an editable address turns it into a general browser
 * pointed at the app's own origin with the person's session in the jar. The
 * address is shown, as text, inside the menu where it can be copied and shared.
 *
 * WHY A FRAME AND NOT A PLATFORM BROWSER. Capacitor's in-app browsers are the
 * obvious reach, and neither one answers this brief: SFSafariViewController has
 * its own address display and its own cookie jar (so the person would have to
 * sign in again — the opposite of what was asked), and neither can carry a header
 * bar of ours with a menu in it. The app runs AT the backend's origin, so a frame
 * is already signed in, already themed, and we own every pixel of the chrome.
 *
 * WHAT THAT COSTS, AND THE TWO GATES THAT PAY FOR IT — everywhere.ts has both
 * arguments in full. A frame at the App's OWN origin can reach the app, which is
 * harmless for a page served by a machine that already holds your credentials
 * and a takeover for one served by anybody else. So at this origin the gate is
 * ownership (`inAppViewGate`), and it is enforced, not assumed. A frame on the
 * artifacts' OWN host cannot reach into the app, and that is what lets a second
 * subject in.
 *
 * ONE VIEWER, TWO SUBJECTS — owner, 2026-10-05: a friend's shared artifact opens
 * in the App's main pane with this same viewer, not in a separate window.
 *
 *   `row`     one of this account's own artifacts: the header menu, the dial,
 *             taking it down — everything an owner may do.
 *   `shared`  an artifact a friend shares: its title, who shares it, Reload,
 *             Close and the door to a browser. NONE of the owner's controls —
 *             they are not this person's to use — and it is framed only from the
 *             artifacts' own host (`sharedViewGate`), which this file asks for
 *             itself rather than taking the caller's word.
 */
export type ArtifactSubject =
  | { row: ArtifactRow; shared?: undefined }
  | { shared: SharedArtifact; row?: undefined }

/** What surrounds the artifact, whichever subject it is. */
type Chrome = {
  onClose: () => void
  /** Whether the side panel is showing — the toggle draws its own state. */
  railOpen: boolean
  /** Show/hide the side panel, which is what grows or shrinks this view. */
  onToggleRail: () => void
}

export function ArtifactViewer(props: ArtifactSubject & Chrome) {
  const { onClose, railOpen, onToggleRail } = props
  /*
   * KEYED BY THE ARTIFACT, so another one is another viewer. On a wide screen the rail
   * stays usable beside this view, and a second row tapped there used to be handed to
   * the SAME instance: its open menu and — the one that matters — its ARMED "Remove"
   * carried over, so the confirming tap removed the artifact that had just appeared
   * instead of the one it was armed for (found in review, 2026-10-05). Nothing a viewer
   * holds about one artifact is true of the next.
   */
  return props.shared ? (
    <SharedArtifactView key={props.shared.hash} shared={props.shared} onClose={onClose} railOpen={railOpen} onToggleRail={onToggleRail} />
  ) : (
    <OwnArtifactView key={props.row.hash} row={props.row} onClose={onClose} railOpen={railOpen} onToggleRail={onToggleRail} />
  )
}

/**
 * THE PAGE AROUND THE ARTIFACT. One header for both subjects, so that its two
 * ends — Back and the side-panel toggle leading, the tools ending the row — are one
 * decision and cannot come to differ between them.
 */
function ViewerChrome({
  title,
  sub,
  tool,
  onReload,
  onClose,
  railOpen,
  onToggleRail,
  children,
}: Chrome & {
  title: string
  /** The line under the title: where this page comes from. */
  sub: ReactNode
  /** The one control between Reload and Close: the owner's menu, or the door out. */
  tool: ReactNode
  onReload: () => void
  children: ReactNode
}) {
  return (
    <div id="artView" role="dialog" aria-modal="true" aria-label={`Artifact: ${title}`}>
      <header className="av-hd">
        {/*
         * BACK, AT THE TOP LEFT — owner, 2026-10-22: 點擊任何一個項目, 則會在右側主顯示匡
         * 裡面顯示 Artifact … 的內容, 顯示匡的左上角有返回頁面的按鈕, 可以讓使用者回到上一個
         * 頁面. The artifact is opened from More's list, which is right under this page; Back
         * shows it again. It was ✕ at the top right (2026-09-21) — one way out, now Back,
         * where every page of More has it.
         */}
        <button type="button" className="av-icon av-back" onClick={onClose} aria-label="Back to Artifacts" title="Back to Artifacts">
          <Icon name="chevron_left" size={22} />
        </button>
        {/*
         * THE SIDE PANEL'S TOGGLE, in the position the agent stage puts it —
         * owner, 2026-09-21: 「讓其左上讓放置 Side panel 的 Toggle button. 這個按鈕
         * 就如同 Agent session 一般, 可以開啟 / 隱藏 Side panel 用來 減少 / 增加
         * Artifact 的可視區域」.
         *
         * Wide only, and CSS decides that rather than a prop: the same
         * `min-width: 900px` that docks the rail is what makes this button mean
         * anything, and two places deciding "wide" is how they come to disagree.
         * On a phone the rail IS the screen, the viewer covers it, and Back is
         * already the way out.
         */}
        <button
          type="button"
          className={`av-icon av-rail${railOpen ? ' on' : ''}`}
          onClick={onToggleRail}
          aria-label={railOpen ? 'Hide the side panel' : 'Show the side panel'}
          aria-pressed={railOpen}
          title={railOpen ? 'Hide the side panel' : 'Show the side panel'}
        >
          <Icon name="menu" size={18} label="Side panel" />
        </button>

        {/* The title block, where a browser would put its address bar. It is not
            a field and carries no input: tapping it does nothing on purpose. */}
        <div className="av-title">
          <span className="av-name" title={title}>{title}</span>
          <span className="av-sub">{sub}</span>
        </div>

        <button type="button" className="av-icon" onClick={onReload} aria-label="Reload" title="Reload">
          <Icon name="refresh" size={19} label="Reload" />
        </button>
        {tool}
      </header>
      {children}
    </div>
  )
}

function OwnArtifactView({ row, onClose, railOpen, onToggleRail }: Chrome & { row: ArtifactRow }) {
  const [menuOpen, setMenuOpen] = useState(false)
  /**
   * TAKING IT DOWN IS TWO TAPS, NEVER ONE — owner, 2026-09-25: 我可以從 Artifact 的
   * 下拉選單來刪除那個 artifact, 也就是解除 Local 端與 BFF 端的連接.
   *
   * `armed` is the confirm step, inline rather than a window.confirm(): this menu lives
   * inside a WKWebView on the Mac and a Capacitor shell on the phone, where a native
   * dialog is a different animal on each and cannot be styled or tested. It disarms on
   * every menu close, so an armed button can never be waiting the next time the menu is
   * opened.
   *
   * WHY CONFIRM AT ALL, when the rest of this sheet does not: the URL does not come
   * back. Re-adding the same folder mints the same hash, but anything already handed out
   * is dead in between, and this control sits one row under "Open in your browser".
   */
  const [armed, setArmed] = useState(false)
  const qc = useQueryClient()
  /** Bumped to re-load the frame — a reload button, without touching history. */
  const [nonce, setNonce] = useState(0)

  /*
   * The same query the rail draws from — ONE cache entry, so the menu cannot
   * disagree with the row behind it. `enabled: false`: the rail owns the
   * fetching, this only subscribes to what is already there and re-renders when
   * it changes, which is what makes the permission dial move.
   */
  const list = useQuery<ArtifactsEverywhere>({ queryKey: ['artifacts'], enabled: false })
  const machine = list.data?.machines.find((m) => m.device_id === row.device_id) ?? null
  const live = machine?.artifacts.find((a) => a.hash === row.hash) ?? row
  const state = artifactState(live, machine?.link ?? null, machine?.reachable ?? 'unknown')
  const gate = inAppViewGate(live, machine)

  /*
   * Esc closes the menu first, then the viewer. One key, two meanings, in the
   * order a person expects — closing the whole page because a dropdown was open
   * loses their place.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (menuOpen) setMenuOpen(false)
      else onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen, onClose])

  /**
   * The removal itself. It asks the MACHINE, because that is the only place that can
   * answer: the item lives in that machine's artifacts.json, and the relay only learns
   * of it through that machine's sync — which is also what severs the far end
   * (`unregister … forget: true` drops the live entry and releases the claim).
   *
   * On success the viewer CLOSES, because what it was showing no longer exists; leaving
   * it open would render a frame pointed at a URL the relay has just stopped serving.
   * The list is invalidated first so the rail loses the row in the same beat.
   */
  const remove = useMutation({
    mutationFn: async () => {
      if (!row.device_id) throw new Error('this artifact has no machine on its row')
      return artifactRemove(row.device_id, row.hash)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['artifacts'] })
      setMenuOpen(false)
      onClose()
    },
  })

  const title = live.summary || live.name || live.hash.slice(0, 8)

  return (
    <ViewerChrome
      title={title}
      sub={
        <>
          <span className={`art-dot art-${state.tone}`} aria-hidden="true" />
          {live.machine} · {state.word}
        </>
      }
      tool={
        <button
          type="button"
          className="av-icon"
          aria-label="Artifact settings"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Settings"
          onClick={() => { setMenuOpen((v) => !v); setArmed(false) }}
        >
          <Icon name="more_horiz" size={20} label="Artifact settings" />
        </button>
      }
      onReload={() => setNonce((n) => n + 1)}
      onClose={onClose}
      railOpen={railOpen}
      onToggleRail={onToggleRail}
    >
      {menuOpen && (
        <>
          {/* Same idiom as the file browser's context menu: a transparent catcher
              so a tap anywhere else dismisses, without a document listener that
              would also eat the press that opened it. */}
          <button
            type="button"
            className="av-shield"
            aria-label="Close settings"
            onClick={() => { setMenuOpen(false); setArmed(false) }}
          />
          <div className="av-menu" role="menu">
            <p className="av-state">
              <span className={`art-dot art-${state.tone}`} aria-hidden="true" />
              <strong>{state.word}</strong> <span className="art-why">{state.why}</span>
            </p>

            <ArtifactSettings row={live} machine={machine} link={machine?.link ?? null} />

            <h4 className="art-h">Elsewhere</h4>
            {/*
             * The one door that leaves the app, and the sentence the owner asked
             * for: 「除非使用 App 外部的 Browser,那需要做身份確認」. A browser outside
             * the app has its own cookie jar, so the relay will send it through
             * the sign-in hop — which is correct, and is worth saying before the
             * tap rather than after it.
             */}
            <a
              className="av-out"
              href={live.url || '#'}
              target="_blank"
              rel="noreferrer"
              aria-disabled={!live.url}
            >
              Open in your browser
              <span className="art-opt-w">
                Outside the app you may be asked to sign in first
              </span>
            </a>

            {/*
              TAKE IT DOWN — owner, 2026-09-25: 我可以從 Artifact 的下拉選單來刪除那個
              artifact, 也就是解除 Local 端與 BFF 端的連接.

              LAST IN THE MENU, and that placement is this file's own rule rather than a
              habit: the close button was moved out of the header's lead for exactly this
              reason — a destructive control must not sit where a thumb is already
              travelling. Everything above it is safe to press by accident.
            */}
            <h4 className="art-h">Take it down</h4>
            <button
              type="button"
              className={`av-remove${armed ? ' armed' : ''}`}
              disabled={remove.isPending}
              onClick={() => {
                if (remove.isPending) return
                if (!armed) {
                  setArmed(true)
                  return
                }
                remove.mutate()
              }}
            >
              {remove.isPending ? 'Removing…' : armed ? 'Really remove — this cannot be undone' : 'Remove this artifact'}
              <span className="art-opt-w">
                {armed
                  ? 'Its URL stops working for everyone you gave it to'
                  : 'Unpublishes it and frees the address on the relay'}
              </span>
            </button>
            {remove.isError && (
              <p className="art-note art-bad-note">
                That did not take: {explainShareError(remove.error, row.machine)}
              </p>
            )}
          </div>
        </>
      )}

      <div className="av-body">
        {gate.ok ? (
          /*
           * `key` carries the reload: changing it remounts the frame, which
           * fetches again without reaching into contentWindow.location (a frame
           * that has navigated to an artifact's own sub-page would throw there
           * under some relay redirects, and a reload button that can throw is
           * worse than none).
           */
          <OwnFrame key={`${live.hash}:${nonce}`} hash={live.hash} url={live.url} title={title} />
        ) : (
          <div className="av-blocked">
            <Icon name="warning" size={26} />
            <p className="av-blocked-why">{gate.why}</p>
            {state.why && state.word !== 'live' && <p className="art-why">{state.why}</p>}
          </div>
        )}
      </div>
    </ViewerChrome>
  )
}

/**
 * ONE OPEN OF ONE OF THIS ACCOUNT'S OWN ARTIFACTS.
 *
 * WHAT TO FRAME IS DECIDED HERE, ONCE — when this mounts — and never again. The
 * parent keys it on the artifact and the reload count, so "once per mount" is
 * "once per open, and again on Reload". Nothing in here follows a cache: a
 * changed `src` is not a repaint, it is a navigation, and the page inside would
 * lose whatever the person was doing in it. (PaneStrip records the same lesson
 * for its own frames.)
 *
 *   app       the share link itself, on the App's host — the frame this viewer
 *             has always drawn, attribute for attribute. Chosen at once whenever
 *             there is nothing to ask the cloud.
 *   asking    artifacts have a host of their own, or the first word on that is
 *             still in flight (owner, 2026-10-05): a ticket for that host is
 *             being fetched, and nothing is framed until it is in.
 *   content   the ticket's address on the artifacts' host.
 *
 * Anything short of a usable ticket ends in `app`, never in an error — see
 * contentHost.ts `ownFrameHere`.
 */
type OwnPlan = { on: 'app' } | { on: 'asking' } | ({ on: 'content' } & ContentFrame)

function OwnFrame({ hash, url, title }: { hash: string; url: string; title: string }) {
  const qc = useQueryClient()
  // The share link as it stood at the moment of opening: the fallback is decided once too.
  const [link] = useState(url)
  const [plan, setPlan] = useState<OwnPlan>(() => (ownFrameNeedsCloud(qc) ? { on: 'asking' } : { on: 'app' }))
  const asking = plan.on === 'asking'
  useEffect(() => {
    if (!asking) return
    let gone = false
    void ownFrameHere(qc, hash, link).then((frame) => {
      if (!gone) setPlan(frame ? { on: 'content', ...frame } : { on: 'app' })
    })
    return () => {
      gone = true
    }
  }, [asking, qc, hash, link])

  if (plan.on === 'asking') return null
  if (plan.on === 'app') {
    /*
     * NO `sandbox`, deliberately, and only reachable for this account's own
     * artifacts — everywhere.ts:inAppViewGate has the whole argument.
     * NO `referrerPolicy` either, here or on the frame below: the relay places
     * an artifact's script-built requests BY REFERER (docs/ARTIFACTS.md §2), so
     * stripping it would break every app that fetches its own API.
     */
    return <iframe className="av-frame" title={title} src={link} allow="clipboard-write; fullscreen" />
  }
  return <iframe className="av-frame" title={title} src={plan.src} sandbox={plan.sandbox} allow={plan.allow} />
}

function SharedArtifactView({ shared, onClose, railOpen, onToggleRail }: Chrome & { shared: SharedArtifact }) {
  /** Bumped to re-load the frame — and a reload is a new open: it is decided again. */
  const [nonce, setNonce] = useState(0)

  // Esc closes the viewer. There is no menu here for it to close first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const title = shared.title || shared.hash.slice(0, 8)

  return (
    <ViewerChrome
      title={title}
      sub={`Shared by ${sharerOf(shared)}`}
      tool={
        /*
         * THE DOOR OUT — owner, 2026-10-05: artifacts keep opening in any browser
         * outside the App, where a person who signs in sees what their account
         * may see. It carries the SHARE LINK on the App's host, never the ticket
         * this viewer frames: a ticket is this person's way in, and a link is
         * what can be pasted anywhere.
         */
        <a
          className="av-icon"
          href={shared.url || '#'}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Open in your browser"
          aria-disabled={!shared.url}
          title="Open in your browser"
        >
          <Icon name="open_in_new" size={18} label="Open in your browser" />
        </a>
      }
      onReload={() => setNonce((n) => n + 1)}
      onClose={onClose}
      railOpen={railOpen}
      onToggleRail={onToggleRail}
    >
      <div className="av-body">
        <SharedFrame key={`${shared.hash}:${nonce}`} shared={shared} title={title} />
      </div>
    </ViewerChrome>
  )
}

/**
 * ONE OPEN OF AN ARTIFACT A FRIEND SHARES. Decided once, like the frame above —
 * and here the reason is not hypothetical: the list this row came from is
 * fetched again when the person returns to the window, and the cloud mints a
 * fresh ticket for every row each time. A frame that followed the list would
 * reload the friend's page on every return.
 *
 * THE GATE IS ASKED HERE, not assumed from whoever opened the viewer. This is the
 * one component that can put somebody else's page on the screen, so it is the
 * one that checks — and what it refuses, it says, with the door out still in the
 * header above it.
 */
function SharedFrame({ shared, title }: { shared: SharedArtifact; title: string }) {
  const qc = useQueryClient()
  const [frame] = useState(() => sharedFrameHere(qc, shared))
  if (!frame.ok) {
    return (
      <div className="av-blocked">
        <Icon name="warning" size={26} />
        <p className="av-blocked-why">{frame.why}</p>
      </div>
    )
  }
  return <iframe className="av-frame" title={title} src={frame.src} sandbox={frame.sandbox} allow={frame.allow} />
}
